import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  Balances,
  ClientTimeOffRequest,
  EditedRecord,
  PendingTimeOffRequest,
  RequestTypes,
  TimeOffRequest
} from '../../../models/TimeOffModels';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EmployeePTO, PTOConfig } from '../../../models/paid-time-off';
import { CompaniesService } from '../../../services/companies.service';
import {
  ClientTimeOffRequestService,
  TimeOffRequestService
} from '../../../services/time-off-request.service';
import { AdminService } from '../../../services/admin.service';
import { DateRange } from '../../../models/date-range';
import { OperatorFunction, Subject, merge, BehaviorSubject, Observable, of } from 'rxjs';
import {
  formatDatePickerValue,
  parseDatePickerValue,
  parseTimeString
} from '../../../utils/datepicker-value-parser';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap } from 'rxjs/operators';
import {
  DoesRangeContainWeekends,
  GetDaysBetweenTwoDates,
  GetHoursBetweenTwoTimes
} from '../../../utils/dateUtils';
import {
  dateRangeComponentValidator,
  timeRangeNotEqualValidator,
  timeRangeValidator
} from '../../../utils/dateRangeValidator';
import { PeopleService } from '../../../services/people.service';
import { PagedPayload } from '../../../models/paged-payload';
import { Person } from '../../../models/person';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { ResetForm } from '@ngxs/form-plugin';
import { Select, Store } from '@ngxs/store';
import { TimeOffRequestsState } from '../../../state/TimeOffRequestsState/TimeOffRequests.state';

@Component({
  selector: 'lib-client-new-request-modal',
  templateUrl: './client-new-request-modal.component.html',
  styleUrls: ['./client-new-request-modal.component.css'],
  standalone: false
})
export class ClientNewRequestModalComponent implements OnInit {
  @Input()
  public clientCode: string;

  @Input()
  public openRequests: PendingTimeOffRequest[] = [];
  @Input()
  public editedRequests: EditedRecord[] = [];
  public form: FormGroup;
  @Input()
  public defaultDay: Date | null = null;
  @Output()
  public modalClosed: EventEmitter<any> = new EventEmitter<any>();

  public timeOffPlans: PTOConfig[] = [];
  public employeePtos: EmployeePTO[] = [];
  public displayPtos: PTOConfig[] = [];

  @Select(TimeOffRequestsState.isDateInPast)
  public showDateMessage: Observable<boolean>;
  @Select(TimeOffRequestsState.showPeriodStartDateWarning)
  public showPeriodStartDateWarning: Observable<boolean>;
  public currentRequestHoursObs: BehaviorSubject<number> | null = null;
  public chosenPlanIdObs = null;
  public balances: Balances[] = [];
  public balanceIsNegative: Observable<boolean>;
  public negativeBalanceAllow = false;
  public showHolidayMessage = false;

  public people: PagedPayload<Person>;

  @ViewChild('typeahead') typeahead: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    private timeOffService: ClientTimeOffRequestService,
    private adminService: AdminService,
    private peopleService: PeopleService,
    private employeeTimeOffService: TimeOffRequestService,
    private store: Store
  ) {}

  get showHoursDisplay() {
    if (this.form == null) return false;
    const ee = this.form.get('employee').value;
    return !(ee == null || typeof ee == 'string');
  }

  get partialDayHours() {
    if (this.form.get('requestType').value != RequestTypes.PartialDay) {
      return 0;
    }
    if (this.form.get('startTime').value == null || this.form.get('endTime').value == null) {
      return 0;
    }
    return this._getHoursBetweenTwoTimes(
      this.form.get('startTime').value,
      this.form.get('endTime').value
    );
  }

  ngOnInit() {
    this.companiesService.getPTOConfigs(this.clientCode).subscribe(ptos => {
      this.timeOffPlans = ptos;
      this.peopleService.searchPeople(this.clientCode).subscribe(people => {
        this.people = people;
        this._buildForm();
      });
    });
  }

  negativeBalance(val) {
    setTimeout(() => {
      this.balanceIsNegative = val;
    }, 0);
  }

  public closeModal() {
    this.modalClosed.emit();
  }

  public isWeekend() {
    const formVal = this.form.value;
    let start: Date;
    let end: Date;

    if (formVal.requestType != RequestTypes.MultipleDays) {
      if (formVal.startDate == null) return false;
      start = parseDatePickerValue(formVal.startDate);
      end = parseDatePickerValue(formVal.startDate);
    } else {
      if (formVal.dateRange?.start == null || formVal.dateRange?.end == null) {
        return false;
      }
      start = formVal.dateRange.start;
      end = formVal.dateRange.end;
    }
    return DoesRangeContainWeekends(start, end);
  }
  clearEmployee() {
    this.form.get('employee').reset();
  }
  search: OperatorFunction<string, readonly Person[]> = text => {
    const debouncedText = text.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup = this.click$.pipe(filter(() => !this.typeahead.isPopupOpen()));
    const inputFocus = this.focus$;

    return merge(debouncedText, inputFocus, clicksWithClosedPopup).pipe(
      mergeMap(term => {
        return this.peopleService.searchPeople(this.clientCode, {
          query: term
        });
      }),
      map(result => {
        return result.Items;
      })
    );
  };
  formatter = (x: Person) => `${x.FirstName} ${x.LastName}`;
  public submitForm() {
    const formVal = this.form.value;
    const plan = this.timeOffPlans.find(x => x.Id == formVal.timeOffType);
    const model: ClientTimeOffRequest = {
      TimeOffType: formVal.timeOffType,
      TimeOffTypeName: plan.Name,
      TimeOffRequestType: formVal.requestType,
      StartDate: formVal.startDate != null ? parseDatePickerValue(formVal.startDate) : null,
      Hours: formVal.hours,
      StartTime: null,
      EndTime: null,
      DateRange:
        formVal.dateRange != null
          ? { Start: formVal.dateRange.start, End: formVal.dateRange.end }
          : null,
      ClientComments: formVal.employeeComments,
      EmployeeComments: null,
      PersonId: formVal.employee.Id
    };
    model.StartTime =
      formVal.startTime != null ? parseTimeString(formVal.startTime, model.StartDate) : null;
    model.EndTime =
      formVal.startTime != null ? parseTimeString(formVal.endTime, model.StartDate) : null;
    this.timeOffService.createRequest(this.clientCode, model).subscribe(_ => {
      this.closeModal();
    });
  }

  private _buildForm() {
    this.form = this.fb.group({
      requestId: this.fb.control(null),
      timeOffType: this.fb.control(null, [Validators.required]),
      requestType: this.fb.control(RequestTypes.SingleDay, [Validators.required]),
      startDate: this.fb.control(formatDatePickerValue(this.defaultDay ?? new Date()), [
        Validators.required
      ]),
      hours: this.fb.control(null, [Validators.required, Validators.min(0)]),
      employeeComments: this.fb.control(null),
      startTime: this.fb.control(null),
      endTime: this.fb.control(null),
      dateRange: this.fb.control(
        {
          start: null,
          end: null
        },
        [dateRangeComponentValidator]
      ),
      employee: this.fb.control(null)
    });

    this.store.dispatch(
      new ResetForm({ path: 'timeOffRequests.NewRequestForm', value: this.form.value })
    );
    this.chosenPlanIdObs = this.form.get('timeOffType').valueChanges;
    this.form.setValidators([
      timeRangeValidator('startTime', 'endTime'),
      timeRangeNotEqualValidator('startTime', 'endTime')
    ]);
    this._setFormValidation(this.form.get('requestType').value, true);
    this.form.get('requestType').valueChanges.subscribe(val => {
      this._setFormValidation(val);
    });
    this.currentRequestHoursObs = new BehaviorSubject(this._initCurrentRequestHours());
    this.form.get('hours').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('startTime').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('endTime').valueChanges.subscribe(val => {
      const hours = this._initCurrentRequestHours();
      this.currentRequestHoursObs.next(hours);
    });
    this.form.get('startDate').valueChanges.subscribe(startDate => {
      if (startDate == null) {
        return;
      }
      const parsed = parseDatePickerValue(startDate);
      const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() - 1);
      const end = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate() + 1);
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    });
    this.form.get('dateRange').valueChanges.subscribe(range => {
      if (range?.start == null || range?.end == null) {
        return;
      }
      const start = range.start;
      const end = range.end;
      this.adminService
        .getHolidays(start, end)
        .pipe(
          map(x => {
            return x.length > 0;
          })
        )
        .subscribe(result => {
          this.showHolidayMessage = result;
        });
    });
    this.form.get('employee').valueChanges.subscribe(val => {
      if (typeof val == 'string' || val == null) {
        return;
      }
      this.form.get('timeOffType').reset();
      this.balanceIsNegative = this.store
        .select(TimeOffRequestsState.getRemainingBalance(val.Id))
        .pipe(
          map(bal => {
            return bal < 0;
          })
        );
      this.employeeTimeOffService.getBalances(this.clientCode, val.Id).subscribe(bal => {
        this.balances = bal;
      });
      this.peopleService.getEmployeePTOs(this.clientCode, val.Id).subscribe(employeePtos => {
        this.employeePtos = employeePtos;
        this.displayPtos = this.timeOffPlans.filter(config => {
          const employeePto = this.employeePtos.find(x => x.IsActive && x.Id == config.Id);
          return config.IsActive && config.IsRequestAllowed && employeePto != null;
        });
      });
    });
    this.form.get('timeOffType').valueChanges.subscribe(val => {
      const plan = this.timeOffPlans.find(x => x.Id == val);
      if (plan == null) {
        return;
      }

      this.negativeBalanceAllow = plan.RequestNegativeBalRule == 0;
    });
  }

  private _getHoursBetweenTwoTimes(startTime: Date | string, endTime: Date | string) {
    const start = typeof startTime == 'string' ? parseTimeString(startTime) : startTime;
    const end = typeof endTime == 'string' ? parseTimeString(endTime) : endTime;
    return +(Math.abs(end.getTime() - start.getTime()) / 36e5).toFixed(2);
  }

  private _getHoursBetweenTwoDates(
    startDate: Date | string,
    endDate: Date | string,
    hours: number
  ) {
    if (typeof startDate == 'string') {
      startDate = new Date(startDate);
    }
    if (typeof endDate == 'string') {
      endDate = new Date(endDate);
    }

    //gets number of milliseconds between the two days.
    const differenceInTime = endDate.getTime() - startDate.getTime();
    //Converts into days. 86400000 = 1000 (number of milliseconds in a second) * 60 (seconds in a minute) * 60 (minutes in an hour) * 24 (hours in a day)
    //Adds 1 because the number of days leaves off the start date, and we need it to be inclusive.
    const numberOfDays = differenceInTime / 86400000 + 1;
    const hoursTaken = numberOfDays * hours;
    return +hoursTaken.toFixed(2);
  }

  private _setFormValidation(val: RequestTypes, initial = false) {
    if (!initial) {
      this.form.get('startDate').reset();
      this.form.get('hours').reset();
      this.form.get('startTime').reset();
      this.form.get('endTime').reset();
      this.form.get('dateRange').reset();
    }
    if (val == RequestTypes.SingleDay) {
      this.form.get('startDate').setValidators(Validators.required);
      this.form.get('startDate').updateValueAndValidity();
      this.form
        .get('hours')
        .setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      this.form.get('hours').updateValueAndValidity();
      this.form.get('startTime').clearValidators();
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').clearValidators();
      this.form.get('endTime').updateValueAndValidity();
      this.form.get('dateRange').clearValidators();
      this.form.get('dateRange').updateValueAndValidity();
    } else if (val == RequestTypes.PartialDay) {
      this.form.get('startDate').setValidators(Validators.required);
      this.form.get('startDate').updateValueAndValidity();
      this.form.get('hours').clearValidators();
      this.form.get('hours').updateValueAndValidity();
      this.form.get('startTime').setValidators(Validators.required);
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').setValidators(Validators.required);
      this.form.get('endTime').updateValueAndValidity();
      this.form.get('dateRange').clearValidators();
      this.form.get('dateRange').updateValueAndValidity();
    } else if (val == RequestTypes.MultipleDays) {
      this.form
        .get('hours')
        .setValidators([Validators.required, Validators.min(1), Validators.max(24)]);
      this.form.get('hours').updateValueAndValidity();
      this.form.get('dateRange').setValidators([Validators.required, dateRangeComponentValidator]);
      this.form.get('dateRange').updateValueAndValidity();
      this.form.get('startDate').clearValidators();
      this.form.get('startDate').updateValueAndValidity();
      this.form.get('startTime').clearValidators();
      this.form.get('startTime').updateValueAndValidity();
      this.form.get('endTime').clearValidators();
      this.form.get('endTime').updateValueAndValidity();
    }
    this.form.updateValueAndValidity();
  }
  private _initCurrentRequestHours() {
    const requestType: RequestTypes = this.form.get('requestType').value;
    switch (requestType) {
      case RequestTypes.PartialDay:
        return GetHoursBetweenTwoTimes(
          this.form.get('startTime').value,
          this.form.get('endTime').value
        );
      case RequestTypes.SingleDay:
        return this.form.get('hours').value;
      case RequestTypes.MultipleDays:
        return (
          this.form.get('hours').value *
          GetDaysBetweenTwoDates(
            this.form.get('dateRange').value?.start,
            this.form.get('dateRange').value?.end
          )
        );
    }
  }
  protected readonly RequestTypes = RequestTypes;
}
